import type { Tower } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { horizontalCap, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { defensiveMasonryFace } from './towerMasonry';

/** Small, light-edged dormer plainly visible on the red medieval Montelbaan roof
 * (museum scan 900–1160 × 3520–3890). It does not add the 1606 octagonal stage/spire.
 * Its dimensions and north-east-facing placement on the harbour side are estimates.
 */
export function buildMontelbaanDormer(
  builder: MeshBuilder,
  frame: Frame,
  tower: Tower,
  styles: { readonly wall: VertexStyle; readonly roof: VertexStyle }
): void {
  if (tower.id !== 'tower-montelbaanstoren') return;
  const local = subFrame(frame, 0, 0, Math.PI / 4);
  const front = tower.radius * 0.72;
  const back = front - 1.4;
  const half = 0.5;
  const rise = tower.radius * 1.2;
  const foot = tower.height + rise * 0.28 - 0.15;
  const ring: Ring = [
    [back, -half],
    [front, -half],
    [front, half],
    [back, half],
  ];
  const opening: VertexStyle = {
    ...styles.wall,
    color: styles.wall.color.map((c) => c * 0.28) as [number, number, number],
  };
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const holes = i === 1 ? [{ x: 0.28, y: 0.3, width: 0.44, height: 0.6, cuts: [] }] : [];
    defensiveMasonryFace(builder, styles.wall, local, a, b, foot, 1.0, holes, opening);
  }
  horizontalCap(builder, styles.wall, local, ring, foot, false);
  gableRoof(builder, styles, local, {
    u0: back,
    u1: front,
    v0: -half,
    v1: half,
    eaveY: foot + 1,
    ridgeY: foot + 1.65,
    ends: { start: true, end: true },
  });
}
